import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, X, Search } from 'lucide-react';
import { cn } from '../../utils/cn';
import Button from './Button';
import Input from './Input';
import { Checkbox } from "./Checkbox";

const MultiSelect = React.forwardRef(({
  className,
  options = [], 
  value = [], 
  placeholder = 'Select options',
  disabled = false,
  required = false,
  label,
  description,
  error,
  searchable = true,
  clearable = true,
  id,
  name,
  onChange,
  onOpenChange,
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const selectId = id || `multi-select-${Math.random().toString(36).substr(2, 9)}`;

  const filteredOptions = searchable && searchTerm
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const getSelectedDisplay = () => {
    const selectedLabels = options.filter(opt => value.includes(opt.value)).map(opt => opt.label);
    if (selectedLabels.length === 0) return placeholder;
    if (selectedLabels.length === 1) return selectedLabels[0];
    return `${selectedLabels.length} selected`;
  };

  const handleToggle = () => {
    if (!disabled) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      onOpenChange?.(newIsOpen);
      if (!newIsOpen) {
        setSearchTerm('');
      }
    }
  };

  const handleOptionToggle = (optionValue, e) => {
    if (e) {
      e.stopPropagation(); // Prevent event bubbling
    }
    
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange?.(newValue);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.([]);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      onOpenChange?.(false);
      setSearchTerm('');
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      {label && (
        <label
          htmlFor={selectId}
          className={cn(
            "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 block",
            error ? "text-destructive" : "text-foreground"
          )}
        >
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <button
        ref={ref}
        id={selectId}
        type="button"
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          error && "border-destructive focus:ring-destructive",
          value.length === 0 && "text-muted-foreground"
        )}
        onClick={handleToggle}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        {...props}
      >
        <span className="truncate">{getSelectedDisplay()}</span>
        <div className="flex items-center gap-1">
          {clearable && value.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-4 w-4 hover:bg-transparent"
              onClick={handleClear}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-md shadow-md">
          {searchable && (
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search options..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 pr-3 py-2 text-sm"
                  onClick={(e) => e.stopPropagation()} // Prevent closing when clicking search
                />
              </div>
            </div>
          )}
          <div className="py-1 max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {searchTerm ? 'No options found' : 'No options available'}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className="flex items-center space-x-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
                  onClick={(e) => handleOptionToggle(option.value, e)}
                >
                  <Checkbox
                    id={`checkbox-${option.value}`}
                    checked={value.includes(option.value)}
                    onCheckedChange={(checked) => {
                      handleOptionToggle(option.value);
                    }}
                    onClick={(e) => e.stopPropagation()} // Prevent double triggering
                  />
                  <label
                    htmlFor={`checkbox-${option.value}`}
                    className="cursor-pointer flex-1"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent double triggering
                      handleOptionToggle(option.value, e);
                    }}
                  >
                    {option.label}
                  </label>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      {description && !error && (
        <p className="text-sm text-muted-foreground mt-1">
          {description}
        </p>
      )}
      {error && (
        <p className="text-sm text-destructive mt-1">
          {error}
        </p>
      )}
    </div>
  );
});

MultiSelect.displayName = 'MultiSelect';

export default MultiSelect;